import { Injectable } from '@angular/core';
import { ClaimsApiService } from '@ecosure-suite/shared/data-access';
import { LogService } from '@ecosure-suite/shared/util-logging';
import { NotificationsService } from '@ecosure-suite/shared/utils-notifications';
import { Parameter, CommonService } from '@ecosure-suite/shared/utils-helpers';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClaimsDocumentsConfiguration } from './tokens';
import {
  ClaimDocument,
  ClaimDocumentForm
} from './+state/claim-documents.models';

@Injectable({
  providedIn: 'root'
})
export class ClaimDocumentsService {
  constructor(
    private apiService: ClaimsApiService,
    private logService: LogService,
    private notificationsService: NotificationsService,
    private configuration: ClaimsDocumentsConfiguration
  ) {}

  getDocumentsByOwner(owner: Parameter): Observable<ClaimDocument[]> {
    return this.apiService
      .get(`/documents?ownerId=${owner.value}&ownerType=${owner.name}`)
      .pipe(
        map(data => {
          if (data.statusCode !== '200' && data.statusCode !== '404') {
            this.notificationsService.showError(
              'Documents error',
              data.message
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return data.statusCode !== '404'
            ? data.responseBody.map(
                document =>
                  ({
                    id: document.documentId,
                    type: document.documentType,
                    owner: {
                      name: document.ownerType,
                      value: document.ownerId
                    },
                    format: CommonService.getFileFormat(document.format),
                    ext: document.format,
                    uri: document.relativeUrl
                      ? this.configuration.url + document.relativeUrl
                      : null,
                    dateCreated: new Date(document.dateCreated)
                  } as ClaimDocument)
              )
            : [];
        })
      );
  }

  saveDocumentInformation(
    form: ClaimDocumentForm
  ): Observable<ClaimDocumentForm> {
    return this.apiService
      .post(`/documents`, {
        ownerId: form.owner.value,
        ownerType: form.owner.name,
        documentName: form.file.name,
        documentType: form.type,
        format: form.file.name.split('.')[1].toUpperCase(),
        createdBy: `kkmarutsi` // Todo: remove createdBy
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              'Failed to upload document'
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return {
            ...form,
            document: {
              id: data.responseBody.documentId,
              type: data.responseBody.documentType,
              owner: {
                name: data.responseBody.ownerType,
                value: data.responseBody.ownerId
              },
              format: CommonService.getFileFormat(data.responseBody.format),
              ext: data.responseBody.format,
              uri: data.responseBody.relativeUrl
                ? this.configuration.url + data.responseBody.relativeUrl
                : null,
              dateCreated: new Date(data.responseBody.dateCreated)
            }
          } as ClaimDocumentForm;
        })
      );
  }

  updateDocumentInformation(
    form: ClaimDocumentForm
  ): Observable<ClaimDocumentForm> {
    return this.apiService
      .put(`/documents`, {
        documentId: form.document.id,
        ownerId: form.owner.value,
        ownerType: form.owner.name,
        documentName: form.file.name,
        documentType: form.type,
        format: form.file.name.split('.')[1].toUpperCase(),
        createdBy: `kkmarutsi` // Todo: remove createdBy
      })
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              'Failed to upload document'
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }

          return {
            ...form,
            document: {
              id: data.responseBody.documentId,
              type: data.responseBody.documentType,
              owner: {
                name: data.responseBody.ownerType,
                value: data.responseBody.ownerId
              },
              format: CommonService.getFileFormat(data.responseBody.format),
              ext: data.responseBody.format,
              uri: data.responseBody.relativeUrl
                ? this.configuration.url + data.responseBody.relativeUrl
                : null,
              dateCreated: new Date(data.responseBody.dateCreated)
            }
          } as ClaimDocumentForm;
        })
      );
  }

  upload(form: ClaimDocumentForm): Observable<ClaimDocument> {
    return this.apiService
      .file(`/documents/upload?documentId=${form.document.id}`, form.file)
      .pipe(
        map(data => {
          if (data.statusCode !== '200') {
            this.notificationsService.showError(
              'Documents error',
              'Failed to upload document'
            );
            this.logService.error(data.message);
            throw new Error(data.message);
          }
          return {
            id: data.responseBody.documentId,
            type: data.responseBody.documentType,
            owner: {
              name: data.responseBody.ownerType,
              value: data.responseBody.ownerId
            },
            format: CommonService.getFileFormat(data.responseBody.format),
            ext: data.responseBody.format,
            uri: data.responseBody.relativeUrl
              ? this.configuration.url + data.responseBody.relativeUrl
              : null,
            dateCreated: new Date(data.responseBody.dateCreated)
          } as ClaimDocument;
        })
      );
  }
}
